import React from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import AppText, { FormLabel } from "../AppText/AppText";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

export interface DefaultOptionType {
	id: string;
	name: string;
}

interface AppSelectProps<O> {
	options?: O[];
	name?: string;
	value?: string;
	onChange?: (value: string) => void;
	label?: string;
	disabled?: boolean;
	errors?: string;
	placeholder?: string;
	getOptionLabel?: (option: O) => string;
	getOptionValue?: (option: O) => string;
	mandatory?: boolean;
	width?: string;
	margin?: string;
	/** Merged onto the dropdown content — e.g. a z-index override when the
	 * select sits inside a dialog/sheet (their overlays are z-[1200], floating
	 * content is z-[1300], above them, so this is rarely needed). */
	contentClassName?: string;
	/**
	 * Optional tooltip ℹ️
	 */
	tooltip?: string | React.ReactNode;
	/**
	 * Optional knowledge base tag to open knowledge base sidebar
	 */
	knowledgeBaseTag?: string;
	/**
	 * Optional custom tooltip text for knowledge base trigger
	 */
	knowledgeBaseTooltip?: string;
	/**
	 * Optional knowledge base tag that only shows on hover (reduces visual clutter)
	 */
	showKnowledgeBaseForTagOnHover?: string;
}

/**
 * Radix throws if a `SelectItem` has `value=""`, which unmounts the whole app
 * (there is no error boundary). Options with an empty id — the "- Ingen -" /
 * "Följ avtalet" idiom several forms use — are therefore rendered with this
 * sentinel and translated back on change, so callers keep working with "".
 */
const EMPTY_OPTION_VALUE = "__EMPTY__";

function AppSelect<O extends Record<string, any>>({
	options = [],
	name,
	value,
	onChange,
	label,
	disabled,
	errors,
	placeholder,
	getOptionLabel,
	getOptionValue,
	mandatory,
	width = "100%",
	margin = "0 0 1rem 0",
	contentClassName,
	tooltip,
	knowledgeBaseTag,
	knowledgeBaseTooltip,
	showKnowledgeBaseForTagOnHover,
}: AppSelectProps<O>) {
	const { t } = useTranslation();

	const optionLabel = (option: O) => {
		if (getOptionLabel) return getOptionLabel(option);
		return option.name || option.label || String(option);
	};

	const optionValue = (option: O) => {
		if (getOptionValue) return getOptionValue(option);
		return option.id || option.value || String(option);
	};

	/** True when the caller offers an explicit "no value" option (id ""). */
	const hasEmptyOption = options.some((option) => !optionValue(option));

	// Ensure value is always a string to keep Select controlled. An empty value
	// means "no selection" (placeholder) unless the caller has an empty option,
	// in which case it selects that option through the sentinel.
	const controlledValue = value ? value : hasEmptyOption ? EMPTY_OPTION_VALUE : "";

	return (
		<div style={{ width, margin }}>
			{label && (
				<FormLabel
					tooltip={tooltip}
					knowledgeBaseTag={knowledgeBaseTag}
					knowledgeBaseTooltip={knowledgeBaseTooltip}
					showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
				>
					{t(label as any)}
					{mandatory ? " *" : ""}
				</FormLabel>
			)}
			<Select
				name={name}
				value={controlledValue}
				onValueChange={(val) => onChange?.(val === EMPTY_OPTION_VALUE ? "" : val)}
				disabled={disabled}
			>
				<SelectTrigger
					// Browser tests: the trigger has no label association to locate by
					data-testid={name ? `select-${name}` : undefined}
					className={cn(
						"h-[2.3125rem] rounded-sm border-solid text-[0.9375rem]",
						"focus:border-blue-900 focus:ring-0",
						errors && "!border-red-500 !bg-red-50"
					)}
				>
					<SelectValue
						placeholder={placeholder ? t(placeholder as any) : t("SELECT_PLACEHOLDER")}
					/>
				</SelectTrigger>
				<SelectContent className={contentClassName}>
					{options.map((option, index) => {
						const optVal = optionValue(option);
						const optLabel = optionLabel(option);

						return (
							<SelectItem
								key={optVal || index}
								value={optVal || EMPTY_OPTION_VALUE}
								className="text-[0.9375rem]"
							>
								{optLabel}
							</SelectItem>
						);
					})}
				</SelectContent>
			</Select>

			{errors && (
				<AppText margin="0.3rem 0 0 0.15rem" size="xs" color="danger">
					{t(errors as any)}
				</AppText>
			)}
		</div>
	);
}

export default AppSelect;

interface AppSelectControllerProps<O> extends AppSelectProps<O> {
	control: Control<any, any>;
	name: string;
	rules?: object;
}

export function AppSelectController<O extends Record<string, any>>({
	control,
	rules,
	name,
	onChange: onChangeExternal,
	...rest
}: AppSelectControllerProps<O>) {
	return (
		<Controller
			name={name}
			rules={rules}
			control={control}
			render={({ field: { value, onChange, ...fieldProps } }) => (
				<AppSelect<O>
					{...rest}
					{...fieldProps}
					value={value}
					onChange={(val) => {
						onChange(val);
						onChangeExternal?.(val);
					}}
				/>
			)}
		/>
	);
}
